/**
 * Zod schemas for ward email invitations (V2-E3, HAN-85). The single source of
 * truth for the *shape and rules* of the create-invitation payload, shared so
 * the invite web UI (E3.3) and the API validate identically — the API's
 * class-validator DTO mirrors these rules field-for-field, and the constants
 * below are imported by both sides so they can't drift.
 *
 * Kept dependency-light: only `zod` + the shared membership roles. No imports
 * from the API or drizzle.
 */
import { z } from "zod";

import { WARD_MEMBER_ROLES } from "./membership.js";

/**
 * Lifecycle of an invitation — mirrors the `invitation_status` Postgres enum
 * (design §4). `pending` is the only live state; the rest are terminal:
 * `accepted` on redeem-at-registration, `revoked` when an admin cancels it,
 * `expired` once it ages past its 7-day window.
 */
export const INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"] as const;
export type InvitationStatusValue = (typeof INVITATION_STATUSES)[number];

/** How long an invitation stays valid before it expires (design §4: 7 days). */
export const INVITATION_TTL_DAYS = 7;

/**
 * Payload for `POST /wards/:wardId/invitations` — invite an email address to a
 * ward at a membership role. The invite *pre-authorizes* membership; it never
 * creates an account (the doctor completes their own GMC-capturing signup). The
 * role defaults to `member`; an admin can invite a fellow admin directly.
 */
export const createInvitationSchema = z.object({
  email: z.string().trim().email("Enter a valid email address").max(254),
  role: z.enum(WARD_MEMBER_ROLES).default("member"),
});

export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;
